import { cn } from "cn";
import { upperCase } from "lodash-es";
import { Button } from "@/components/ui/button/button";
import { BwTagLine } from "@/components/ui/icons";

type OfferStatusRowProps = {
  code: string;
  /** What the offer did, in plain language — e.g. "100% off". */
  description?: string | null;
  onRemove: () => void;
  isRemoving?: boolean;
  className?: string;
};

/**
 * The applied-offer row: a status line, not a money row.
 *
 * The amount deliberately lives in the ledger's own `Offer discount` row instead of
 * here. When this row carried the figure it sat BELOW the total that already had the
 * discount baked in, where every neighbouring row adds — so it read as a second
 * reduction that never arrived.
 */
export function OfferStatusRow({
  code,
  description,
  onRemove,
  isRemoving = false,
  className,
}: OfferStatusRowProps) {
  return (
    <div
      className={cn(
        /* sorted */
        "flex",
        "items-center justify-between gap-2",
        "px-4 py-3",
        isRemoving && "opacity-50",
        className,
      )}
    >
      <div className="flex items-center gap-1 min-w-0 text-sm">
        <BwTagLine className="text-text-success size-4 shrink-0" />
        <p className="truncate text-text-dark-high text-xs">
          {upperCase(code)}
          {description ? ` - ${description}` : ""}
        </p>
      </div>
      <Button
        onClick={onRemove}
        disabled={isRemoving}
        size="xs"
        variant="neutral"
        kind="link"
        className="text-text-dark-low px-0 no-underline text-xs"
      >
        Remove
      </Button>
    </div>
  );
}
